import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useOpalUser } from '@frontend/hooks';

import {
  bulkToggleChatEnabled,
  bulkToggleTools,
  getTools,
  restoreToolsInPrimaryAgent,
  toggleToolEnabledInPrimaryAgent,
  toggleToolStatus,
} from '../services/toolsService';

export function useGetTools(instanceId: string, include_disabled = false) {
  const {
    data: toolsResponse,
    isPending,
    error,
  } = useQuery({
    queryKey: getTools({ instanceId, include_disabled }).key,
    queryFn: getTools({ instanceId, include_disabled }),
    enabled: !!instanceId,
  });

  return { toolsResponse, isPending, error };
}

export function useToolStatus() {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const { mutate, isPending, error, variables } = useMutation({
    mutationFn: ({
      toolId,
      toolName: _toolName,
      isDisabled,
    }: {
      toolId: string;
      toolName: string;
      isDisabled: boolean;
    }) =>
      toggleToolStatus({
        function_id: toolId,
        user_id: opalUser?.id,
        instanceId: opalUser?.instance.instance_id,
        is_disabled: isDisabled,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: opalUser?.instance.instance_id, include_disabled: true })
          .key,
      });
      toaster.create(
        `Tool ${variables.toolName} ${variables.isDisabled ? 'activated' : 'deactivated'} successfully`,
        { type: 'success' }
      );
    },
    onError: err => {
      toaster.create('Failed to update tool status', { type: 'danger' });
      console.error('Error updating tool status:', err);
    },
  });

  return { toggleStatus: mutate, isUpdating: isPending, error, updatingToolId: variables?.toolId };
}

export function useToolEnabledInPrimaryAgent() {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const { mutate, isPending, error, variables } = useMutation({
    mutationFn: ({
      toolName,
      isEnabledInPrimaryAgent,
    }: {
      toolName: string;
      isEnabledInPrimaryAgent: boolean;
    }) =>
      toggleToolEnabledInPrimaryAgent({
        toolName,
        instanceId: opalUser?.instance.instance_id,
        isEnabledInPrimaryAgent,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: opalUser?.instance.instance_id, include_disabled: true })
          .key,
      });
      toaster.create(
        `Tool ${variables.toolName} ${
          variables.isEnabledInPrimaryAgent ? 'disabled' : 'enabled'
        } in chat successfully`,
        { type: 'success' }
      );
    },
    onError: (err, variables) => {
      toaster.create(
        `Failed to ${
          variables.isEnabledInPrimaryAgent ? 'disable' : 'enable'
        } tool ${variables.toolName} in chat`,
        { type: 'danger' }
      );
      console.error(
        `Error ${
          variables.isEnabledInPrimaryAgent ? 'disabling' : 'enabling'
        } tool ${variables.toolName} in Opal Chat:`,
        err
      );
    },
  });

  return {
    toggleEnabledInPrimaryAgent: mutate,
    isUpdating: isPending,
    error,
    updatingToolId: variables?.toolName,
  };
}

export function useRestoreToolsInPrimaryAgent() {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const { mutate, isPending, error } = useMutation({
    mutationFn: () => restoreToolsInPrimaryAgent({ instanceId: opalUser?.instance.instance_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: opalUser?.instance.instance_id, include_disabled: true })
          .key,
      });
      toaster.create(`Tools in Opal Chat restored successfully`, { type: 'success' });
    },
    onError: err => {
      toaster.create(`Failed to restore tools in Opal Chat`, { type: 'danger' });
      console.error(`Error restoring tools in Opal Chat:`, err);
    },
  });

  return {
    restoreToolsInPrimaryAgent: mutate,
    isRestoring: isPending,
    error,
  };
}

export function useBulkToggleTools() {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const { mutate, isPending, error, variables } = useMutation({
    mutationFn: ({
      functionIds,
      action,
    }: {
      functionIds: string[];
      action: 'enable' | 'disable';
    }) =>
      bulkToggleTools({
        instanceId: opalUser?.instance.instance_id,
        functionIds,
        userId: opalUser?.id,
        action,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: opalUser?.instance.instance_id, include_disabled: true })
          .key,
      });
      toaster.create(`${variables.functionIds.length} tool(s) ${variables.action}d successfully`, {
        type: 'success',
      });
    },
    onError: (err, variables) => {
      toaster.create(`Failed to ${variables.action} tools`, { type: 'danger' });
      console.error(`Error ${variables.action}ing tools:`, err);
    },
  });

  return {
    bulkToggle: mutate,
    isToggling: isPending,
    error,
    togglingServiceId: variables?.functionIds,
  };
}

export function useBulkToggleChatEnabled() {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ toolNames, action }: { toolNames: string[]; action: 'enable' | 'disable' }) =>
      bulkToggleChatEnabled({
        instanceId: opalUser?.instance.instance_id,
        toolNames,
        action,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getTools({ instanceId: opalUser?.instance.instance_id, include_disabled: true })
          .key,
      });
      toaster.create(
        `${variables.toolNames.length} tool(s) ${variables.action}d in chat successfully`,
        { type: 'success' }
      );
    },
    onError: (err, variables) => {
      toaster.create(`Failed to ${variables.action} tools in chat`, { type: 'danger' });
      console.error(`Error ${variables.action}ing tools in chat:`, err);
    },
  });

  return {
    bulkToggleChatEnabled: mutate,
    isToggling: isPending,
    error,
  };
}
